/**
 * Shared, client-safe helpers for building TanStack route `head()` objects.
 *
 * Every indexable route calls buildHead() to emit exactly one of each required
 * meta tag (title, description, robots, canonical, og:title/description/url/
 * image/type, twitter card/title/description/image) plus optional JSON-LD
 * scripts. This is rendered into the initial server HTML by <HeadContent /> in
 * the root shell, so it is present before hydration — no client-only helmet
 * duplication.
 */
import {
  SITE_URL,
  SITE_NAME,
  DEFAULT_OG_IMAGE,
  ORG_ID,
  ORGANIZATION_SCHEMA,
} from "./seo";

export interface HeadInput {
  title: string;
  description: string;
  /** Root-relative canonical path, e.g. "/about-us" or "/services/interior-design". */
  path: string;
  ogImage?: string;
  ogType?: string;
  noIndex?: boolean;
  schemaJson?: object | object[];
}

function abs(path: string): string {
  const normalized = path.startsWith("/") ? path : `/${path}`;
  return `${SITE_URL}${normalized === "/" ? "/" : normalized}`;
}

/**
 * Build a TanStack `head()` return value with the full, deduplicated metadata
 * set for an indexable route.
 */
export function buildHead(input: HeadInput) {
  const url = abs(input.path);
  const image = input.ogImage || DEFAULT_OG_IMAGE;
  const noIndex = input.noIndex ?? false;
  const ogType = input.ogType || "website";

  const meta: Record<string, string | undefined>[] = [
    { title: input.title },
    { name: "description", content: input.description },
    {
      name: "robots",
      content: noIndex ? "noindex, nofollow" : "index, follow",
    },
    { property: "og:title", content: input.title },
    { property: "og:description", content: input.description },
    { property: "og:url", content: url },
    { property: "og:type", content: ogType },
    { property: "og:image", content: image },
    { property: "og:site_name", content: SITE_NAME },
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: input.title },
    { name: "twitter:description", content: input.description },
    { name: "twitter:image", content: image },
  ];

  const links: Record<string, string | undefined>[] = [
    { rel: "canonical", href: url },
  ];

  const scripts: { type: string; children: string }[] = [];
  if (input.schemaJson) {
    const schemas = Array.isArray(input.schemaJson)
      ? input.schemaJson
      : [input.schemaJson];
    for (const schema of schemas) {
      scripts.push({
        type: "application/ld+json",
        children: JSON.stringify(schema),
      });
    }
  }

  return { meta, links, scripts };
}

/** Organization @id constant for JSON-LD references. */
export { ORG_ID, SITE_URL, ORGANIZATION_SCHEMA };
